import React, { useState, useEffect } from "react";
import toast from "react-hot-toast";
import { Modal, Button, FormInput, FormTextarea } from "../../../components/ui";
import * as quotationTermService from "../services/quotationTermService";

/**
 * QuotationTermModal - Add/Edit Quotation Terms & Conditions.
 */
export const QuotationTermModal = ({ isOpen, onClose, onSuccess, initialData }) => {
    const [formData, setFormData] = useState({
        content: "",
        isActive: true
    });

    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (initialData) {
            setFormData({
                content: initialData.content || "",
                isActive: initialData.isActive !== undefined ? initialData.isActive : true
            });
        } else {
            setFormData({
                content: "",
                isActive: true
            });
        }
    }, [initialData, isOpen]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({ 
            ...prev, 
            [name]: type === 'checkbox' ? checked : value 
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            setIsSubmitting(true);
            
            if (initialData?._id) {
                await quotationTermService.updateTerm(initialData._id, formData);
                toast.success("Quotation clause updated!");
            } else {
                await quotationTermService.createTerm(formData);
                toast.success("Quotation clause added!");
            }
            onSuccess();
            onClose();
        } catch (err) {
            toast.error(err.response?.data?.message || "Something went wrong");
        } finally {
            setIsSubmitting(false);
        }
    };

    const footer = (
        <div className="flex gap-3 w-full">
            <Button variant="secondary" onClick={onClose} fullWidth className="rounded-lg font-bold">
                Cancel
            </Button>
            <Button 
                type="submit" 
                variant="orange" 
                loading={isSubmitting} 
                onClick={handleSubmit}
                fullWidth 
                className="rounded-lg font-bold shadow-lg shadow-orange-100/50"
            >
                {initialData ? "Update Clause" : "Add Clause"}
            </Button>
        </div>
    );

    return (
        <Modal 
            isOpen={isOpen} 
            onClose={onClose} 
            title={initialData ? "Edit Quotation Clause" : "Add Quotation Clause"}
            size="md"
            footer={footer}
        >
            <form onSubmit={handleSubmit} className="space-y-5">
                <FormTextarea 
                    label="Terms & Conditions Content" 
                    field="content" 
                    value={formData.content} 
                    onChange={handleChange} 
                    placeholder="Type the legal terms here..."
                    rows={12}
                    required 
                />

                <div className="flex items-center gap-3 py-2 bg-slate-50/50 p-3 rounded-xl border border-slate-100">
                    <input 
                        type="checkbox" 
                        id="isActiveToggle"
                        name="isActive" 
                        checked={formData.isActive} 
                        onChange={handleChange}
                        className="w-4 h-4 rounded border-slate-300 text-brand-blue focus:ring-brand-blue/20"
                    />
                    <label htmlFor="isActiveToggle" className="text-[13px] font-bold text-slate-700 cursor-pointer">
                        Enable this clause for use in Quotations
                    </label>
                </div>
            </form>
        </Modal>
    );
};
